

import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Calendar, CheckCircle2, XCircle, Infinity, Package, Activity, Users, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { usePackageLimits } from '@/hooks/usePackageLimits';

export function SubscriptionLimitsCard() {
    const { packageUsage, loading, error } = usePackageLimits();

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (error || !packageUsage) {
        return (
            <div className="flex items-center justify-center py-8 text-destructive">
                <XCircle className="w-5 h-5 mr-2" />
                {error || 'Failed to load subscription data'}
            </div>
        );
    }

    if (!packageUsage.subscription) {
        return (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Package className="w-5 h-5 mr-2" />
                No active subscription
            </div>
        );
    }

    const { subscription: subscriptionData, limits } = packageUsage;
    const isActive = subscriptionData.status === 'ACTIVE';
    const validUpto = new Date(subscriptionData.validUpto);

    const getUsageColor = (current: number, limit: number, unlimited: boolean) => {
        if (unlimited) return 'text-blue-600 dark:text-blue-400';
        const percentage = (current / limit) * 100;
        if (percentage >= 100) return 'text-red-600 dark:text-red-400';
        if (percentage >= 80) return 'text-orange-600 dark:text-orange-400';
        return 'text-green-600 dark:text-green-400';
    };

    const getProgressValue = (current: number, limit: number, unlimited: boolean) => {
        if (unlimited) return 0;
        return Math.min((current / limit) * 100, 100);
    };

    const renderLimitItem = (
        icon: React.ComponentType<{ className?: string }>,
        label: string,
        limit: { limit: number; current: number; unlimited: boolean; canCreate?: boolean; canInvite?: boolean }
    ) => {
        const Icon = icon;
        const canPerformAction = limit.canCreate ?? limit.canInvite ?? true;
        const usageColor = getUsageColor(limit.current, limit.limit, limit.unlimited);

        return (
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {limit.unlimited ? (
                            <div className="flex items-center gap-1">
                                <Infinity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                <span className={`text-sm font-semibold ${usageColor}`}>
                                    {limit.current}
                                </span>
                            </div>
                        ) : (
                            <span className={`text-sm font-semibold ${usageColor}`}>
                                {limit.current} / {limit.limit}
                            </span>
                        )}
                        {/* {canPerformAction ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                        ) : (
                            <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                        )} */}
                    </div>
                </div>
                {/* {!limit.unlimited && (
                    <Progress
                        value={getProgressValue(limit.current, limit.limit, limit.unlimited)}
                        className={`h-2 ${limit.current >= limit.limit
                                ? 'bg-red-200 dark:bg-red-900/20'
                                : limit.current / limit.limit >= 0.8
                                    ? 'bg-orange-200 dark:bg-orange-900/20'
                                    : 'bg-green-200 dark:bg-green-900/20'
                            }`}
                    />
                )} */}
            </div>
        );
    };

    return (
        <>
            <div className="space-y-3 pb-4 border-b border-border">
                <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Package</span>
                    <Badge variant="outline" className="font-semibold">
                        {packageUsage.subscription.packageName}
                    </Badge>
                </div>
                {/* <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Type</span>
            <span className="text-sm font-medium capitalize">{subscription.packageType}</span>
          </div> */}
                {/* Community's validUpto is a far-future placeholder, not a real expiry. */}
                {subscriptionData.packageCode !== 'COMMUNITY' && (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Valid Until</span>
                        </div>
                        <span className="text-sm font-medium">
                            {format(validUpto, 'MMM dd, yyyy')}
                        </span>
                    </div>
                )}
            </div>

            <div className="space-y-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                    {/* <Activity className="w-4 h-4 text-muted-foreground" /> */}
                    <h4 className="text-sm font-semibold">Usage Limits</h4>
                </div>

                {renderLimitItem(Activity, 'Scans', limits.scans)}
                {renderLimitItem(Users, 'Team Members', limits.teamMembers)}
                {renderLimitItem(Upload, 'Manual Uploads', limits.ortUploads)}
            </div>

            {/* {(!limits.scans.canCreate ||
                !limits.teamMembers.canInvite) && (
                    <div className="flex items-start gap-2 p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg">
                        <XCircle className="w-4 h-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-orange-800 dark:text-orange-200">
                            You've reached the limit for one or more resources. Upgrade your plan to continue.
                        </p>
                    </div>
                )} */}
        </>
    );
}
